import { forwardRef, ReactElement, Ref, useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { RemoteParticipant } from 'twilio-video';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { Transitions } from '../Transitions';
import { useSignalrConnection, useTeleconsent, useVideo } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { parseParticipantIdentity, withOutsideClickPreventer } from 'utils';
import { Room } from './components';
import { ISignalrConnectionContext, RoomRightSidebarProvider } from 'context';

import styles from './VideoRoom.module.scss';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement;
  },
  ref: Ref<unknown>,
) {
  return <Transitions className={styles.transition} direction='up' type='slide' ref={ref} {...props} />;
});

export function VideoRoom(): JSX.Element {
  const { t } = useTranslation('providerRoom');
  const user = useAppSelector(selectUser);
  const { enqueueSnackbar } = useSnackbar();
  const { callEnded, isRequeuing }: ISignalrConnectionContext = useSignalrConnection();
  const [open, setOpen] = useState(false);
  const { room } = useVideo();
  const { isOpen: isTeleconsentOpen } = useTeleconsent();

  useEffect(() => {
    if (room) {
      setOpen(true);
      const onDisconnected = () => {
        setOpen(false);
        callEnded();
      };

      const participantDisconnected = (participant: RemoteParticipant) => {
        const [id, identity] = parseParticipantIdentity(participant);
        if (!identity || !id) return;
        const [hostId] = (room?.name || '').split('__');
        if (id === hostId) {
          room!.disconnect();
        } else if (!isRequeuing.current) {
          enqueueSnackbar(t('participantLeftCall', { identity }), { variant: 'info' });
        }
      };

      const participantConnected = (participant: RemoteParticipant) => {
        const [, identity] = parseParticipantIdentity(participant);
        enqueueSnackbar(t('participantJoinedCall', { identity }), { variant: 'info' });
      };

      room.on('participantConnected', participantConnected);
      room.on('disconnected', onDisconnected);
      room.on('participantDisconnected', participantDisconnected);
      return () => {
        room.off('participantConnected', participantConnected);
        room.off('disconnected', onDisconnected);
        room.off('participantDisconnected', participantDisconnected);
      };
    }
  }, [user, room, t]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <RoomRightSidebarProvider>
      <Dialog
        fullScreen
        open={open}
        onClose={withOutsideClickPreventer(handleClose)}
        TransitionComponent={Transition}
        disableEnforceFocus={isTeleconsentOpen}
      >
        {room && <Room />}
      </Dialog>
    </RoomRightSidebarProvider>
  );
}
